import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import { NumberField, Select } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import useGridAndSystemProtectionMutation from '../../hooks/use-grid-and-system-protection-mutation'
import useSyncFormWithValues from '../../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../../types/common'
import { ReactivePowerSettingsModel } from '../../types/grid-and-system-protection.model'
import { getReactivePowerSettingsSchema, ReactivePowerSettingsForm } from '../../types/schemas'
import { getChangedFieldStyle, isFieldDisabled } from '../../utils'
import { NAME_SPACE } from './constants'
import { getAvailableFunctionOptions, getDefaultValues } from './helpers'
import ReactivePowerSettingsTable from './ReactivePowerSettingsTable'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
type Props = {
  reactivePowerSettingsModel: ReactivePowerSettingsModel
}

const ReactivePowerSettings: React.FC<Props> = ({ reactivePowerSettingsModel }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights
  const defaultValues = useMemo(() => getDefaultValues(reactivePowerSettingsModel), [reactivePowerSettingsModel])
  const methods = useForm<ReactivePowerSettingsForm>({
    resolver: zodResolver(getReactivePowerSettingsSchema(reactivePowerSettingsModel, t)),
    defaultValues,
    mode: 'onBlur',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { updateGridAndSystemProtection, isLoading } = useGridAndSystemProtectionMutation()

  const availableFunctions = getAvailableFunctionOptions(reactivePowerSettingsModel.fulcrums)

  const onSubmit = useCallback(
    async (data: ReactivePowerSettingsForm): Promise<void> => {
      const { minCosPhi, ...rest } = data

      const promises = []
      const filteredDirtyFields = Object.fromEntries(
        Object.entries(methods.formState.dirtyFields)
          .filter(([key]) => key !== 'minCosPhi')
          .map(entries => entries)
      )

      // ? only update api, if there are changes in the form except minCosPhi, because minCosPhi has its own endpoint and we want to avoid unnecessary calls to the update endpoint
      if (Object.values(filteredDirtyFields).some(Boolean)) {
        promises.push(
          updateGridAndSystemProtection({ reactivePowerSettings: rest }, FUNCTION_NUMBER.reactivePowerSettings).catch(() =>
            methods.reset({ ...methods.formState.defaultValues, minCosPhi })
          )
        )
      }
      if (data.minCosPhi !== methods.formState.defaultValues?.minCosPhi) {
        promises.push(
          updateGridAndSystemProtection({ reactivePowerSettings: { minCosPhi } }, FUNCTION_NUMBER.minCosPhiSettings).catch(() =>
            methods.reset({ ...methods.formState.defaultValues, minCosPhi })
          )
        )
      }

      await Promise.all(promises).then(() => {
        methods.reset(data)
      })
    },
    [methods, updateGridAndSystemProtection]
  )

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)} accordionDetailsSx={{ padding: 0 }}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              {availableFunctions.length > 0 && (
                <SettingsBox
                  title={t(`${NAME_SPACE}.function-reactive-power.title`)}
                  suffix={
                    <Select
                      name="selectedFunction"
                      options={availableFunctions}
                      onChange={() => {
                        methods.clearErrors('fulcrums') // we want to show only the errors of the selected function
                      }}
                      disabled={!canEdit}
                    />
                  }
                />
              )}

              <SettingsBox
                title={t(`${NAME_SPACE}.three-tau.title`)}
                suffix={
                  <NumberField
                    name="threeTau"
                    suffix={UnitTypes.second}
                    disabled={!canEdit || isFieldDisabled(reactivePowerSettingsModel.threeTau)}
                    sx={getChangedFieldStyle(reactivePowerSettingsModel.threeTau.changed)}
                  />
                }
              />

              <ReactivePowerSettingsTable reactivePowerSettingsModel={reactivePowerSettingsModel} />
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

export default ReactivePowerSettings
